import { apiClient } from './apiClient';
import type { Organization, OrganizationStorageCredential } from '@/types/organization';
import { camelize } from './_caseTransform';

/**
 * Read-side service for `/api/organizations/`. The list endpoint is
 * server-filtered to the orgs the caller has access to (system admins
 * see everything; otherwise scoped READER on the org or higher).
 *
 * The API returns snake_case keys; we normalize to the portal's camelCase
 * Organization shape here so callers don't have to reason about casing.
 */

interface ApiOrganization {
  allowed_cloud_region_ids?: string[];
  id: string;
  name: string;
  label: string;
  domain: string;
  plan_id: string;
  default_cloud_region_id?: string | null;
  trial_credit_usd?: string | number | null;
  trial_credit_expires_at?: string | null;
  trial_ended_at?: string | null;
  allow_system_infrastructure?: boolean | null;
  allow_self_managed_infrastructure?: boolean | null;
  disable_cross_cloud_catalog_mounts?: boolean | null;
  disable_cross_region_catalog_mounts?: boolean | null;
  support_slack_webhook_configured?: boolean | null;
  billing_exempt?: boolean | null;
  domain_verified?: boolean | null;
  sev1_blocked?: boolean | null;
  suspended_at?: string | null;
  suspended_reason?: string | null;
  terms_accepted_version?: string | null;
  terms_accepted_at?: string | null;
  idle_stop_required?: boolean | null;
  idle_stop_required_override?: boolean | null;
  created_by_user_id: string;
  created_at: string;
  updated_by_user_id: string;
  updated_at: string;
  member_count?: number;
}

const normalize = (api: ApiOrganization): Organization => ({
  id: api.id,
  name: api.name,
  label: api.label,
  domain: api.domain,
  planId: api.plan_id,
  defaultCloudRegionId: api.default_cloud_region_id ?? undefined,
  trialCreditUsd: api.trial_credit_usd ?? null,
  trialCreditExpiresAt: api.trial_credit_expires_at ?? null,
  trialEndedAt: api.trial_ended_at ?? null,
  allowSystemInfrastructure: api.allow_system_infrastructure ?? undefined,
  allowSelfManagedInfrastructure: api.allow_self_managed_infrastructure ?? undefined,
  disableCrossCloudCatalogMounts: api.disable_cross_cloud_catalog_mounts ?? undefined,
  allowedCloudRegionIds: api.allowed_cloud_region_ids ?? [],
  disableCrossRegionCatalogMounts: api.disable_cross_region_catalog_mounts ?? undefined,
  supportSlackWebhookConfigured: api.support_slack_webhook_configured ?? undefined,
  billingExempt: api.billing_exempt ?? undefined,
  domainVerified: api.domain_verified ?? false,
  sev1Blocked: api.sev1_blocked ?? undefined,
  suspendedAt: api.suspended_at ?? null,
  suspendedReason: api.suspended_reason ?? null,
  termsAcceptedVersion: api.terms_accepted_version ?? null,
  termsAcceptedAt: api.terms_accepted_at ?? null,
  // An older API without idle stop: never required.
  idleStopRequired: api.idle_stop_required ?? false,
  idleStopRequiredOverride: api.idle_stop_required_override ?? null,
  createdByUserId: api.created_by_user_id,
  createdAt: api.created_at,
  updatedByUserId: api.updated_by_user_id,
  updatedAt: api.updated_at,
  memberCount: api.member_count,
});

export const organizationService = {
  /**
   * List every Organization the authenticated user can read. Returns []
   * on success-with-no-rows or when the apiClient falls back to mocks
   * (i.e. anonymous request / network failure / demo mode).
   */
  async list(): Promise<Organization[]> {
    const response = await apiClient.getAll<ApiOrganization>('/organizations/', []);
    return (response.data ?? []).map(normalize);
  },

  async get(id: string): Promise<Organization | null> {
    const response = await apiClient.get<ApiOrganization>(`/organizations/${id}`, undefined);
    return response.data ? normalize(response.data) : null;
  },

  /**
   * Create a new Organization. POSTs to `/organizations/`; apiClient
   * snake_cases the camelCase body to the API's OrganizationCreate fields
   * (name/label/domain/plan_id/default_cloud_region_id/
   * allow_system_infrastructure/allow_self_managed_infrastructure/
   * signup_invite_code). `planId` is optional and defaults to the Free plan
   * server-side. The response is snake_case, so we run it through normalize().
   *
   * Surfaces the apiClient error verbatim ("HTTP <status>: <detail>") — callers
   * should strip the prefix and toast the detail. Expected failures:
   *  - 403 when BETA_INVITE_REQUIRED is set and this is the user's first org
   *    without a valid `signupInviteCode`.
   *  - 409 on a duplicate name / label / domain.
   */
  async create(payload: {
    name: string;
    label?: string;
    domain: string;
    planId?: string;
    defaultCloudRegionId: string;
    allowSystemInfrastructure?: boolean;
    allowSelfManagedInfrastructure?: boolean;
    signupInviteCode?: string;
    /** Explicit acceptance of the Terms of Service + Privacy Policy (required on the public cloud). */
    termsAccepted?: boolean;
    /** System admins only: never billed, no plan quota, starts on Free. */
    billingExempt?: boolean;
  }): Promise<Organization> {
    const response = await apiClient.post<ApiOrganization>('/organizations/', payload);
    return normalize(response.data);
  },

  /** The organization's own managed-catalog buckets, one per cloud/region (keys never returned). */
  async listStorageCredentials(id: string): Promise<OrganizationStorageCredential[]> {
    const response = camelize(await apiClient.get<OrganizationStorageCredential[]>(
      `/organizations/${encodeURIComponent(id)}/storage-credentials`));
    return response.data ?? [];
  },

  /** System admins: empty and delete one of the organization's buckets (refused while a catalog uses it). */
  async deleteStorageCredential(id: string, credentialId: string): Promise<void> {
    await apiClient.delete(`/organizations/${encodeURIComponent(id)}/storage-credentials/${encodeURIComponent(credentialId)}`);
  },

  /** What deleting the organization would remove (org admins / system admins). */
  async deletionPreview(id: string): Promise<OrganizationDeletionPreview> {
    return camelize(await apiClient.get<OrganizationDeletionPreview>(
      `/organizations/${encodeURIComponent(id)}/deletion-preview`)).data;
  },

  /** Delete the organization and EVERYTHING it owns (clusters, catalogs + managed data, database
   *  files, storage buckets, members, subscription). `confirm` is the organization's name, typed.
   *  Can take minutes (cluster and catalog teardown), hence the long timeout. */
  async deleteEverything(id: string, confirm: string): Promise<OrganizationDeletionResult> {
    return camelize(await apiClient.delete<OrganizationDeletionResult>(
      `/organizations/${encodeURIComponent(id)}?cascade=true&confirm=${encodeURIComponent(confirm)}`,
      undefined, { timeoutMs: 15 * 60 * 1000 })).data;
  },

  /** Record the organization's acceptance of the CURRENT Terms of Service (org admins). */
  async acceptTerms(id: string): Promise<Organization> {
    const response = await apiClient.post<ApiOrganization>(`/organizations/${encodeURIComponent(id)}/accept-terms`, {});
    return normalize(response.data);
  },

  /**
   * Update an existing Organization. PATCHes `/organizations/{id}` with only
   * the changed fields (apiClient snake_cases the keys). The API gates the
   * `allow_*` infrastructure flags to system admins and 403s otherwise;
   * org-admins may change name/label/domain/default_cloud_region_id. The
   * snake_case response is normalized back to the portal's Organization shape.
   */
  async patch(
    id: string,
    updates: {
      name?: string;
      label?: string;
      domain?: string;
      /** `null` clears the organization default region. */
      defaultCloudRegionId?: string | null;
      /** Replace the org's cloud/region allow-list; [] clears it (= all regions). 409 if a removed region still hosts live resources. */
      allowedCloudRegionIds?: string[];
      planId?: string;
      allowSystemInfrastructure?: boolean;
      allowSelfManagedInfrastructure?: boolean;
      /** Org-admin editable catalog mount locality governance (egress-cost guard). */
      disableCrossCloudCatalogMounts?: boolean;
      disableCrossRegionCatalogMounts?: boolean;
      /** System-admin only. Write-only Slack incoming-webhook override for
       *  support-ticket notifications; empty string clears it. Never echoed
       *  back — the response carries only supportSlackWebhookConfigured. */
      supportSlackWebhookUrl?: string;
      /** System-admin only. Concurrent-vCPU cap for this org, overriding its plan's;
       *  `null` falls back to the plan. */
      maxCpuQuotaOverride?: number | null;
      /** System-admin only. */
      billingExempt?: boolean;
      /** System admins: grant / top up card-free trial credit (dollars) and its expiry. */
      trialCreditUsd?: number;
      trialCreditExpiresDays?: number;
      /** Org admins: budget cap — stop clusters and pause starts once the credit is used up. */
      stopWhenCreditExhausted?: boolean;
      /** System admins only: true = idle stop required, false = not required,
       *  null = the default rule (required during a trial). */
      idleStopRequiredOverride?: boolean | null;
    },
  ): Promise<Organization> {
    // Send only the keys the caller actually set so we don't clobber unchanged
    // fields and don't trip the system-admin gate on allow_* unnecessarily.
    const body: Record<string, unknown> = {};
    if (updates.name !== undefined) body.name = updates.name;
    if (updates.label !== undefined) body.label = updates.label;
    if (updates.domain !== undefined) body.domain = updates.domain;
    if (updates.defaultCloudRegionId !== undefined) body.defaultCloudRegionId = updates.defaultCloudRegionId;
    if (updates.allowedCloudRegionIds !== undefined) body.allowedCloudRegionIds = updates.allowedCloudRegionIds;
    if (updates.planId !== undefined) body.planId = updates.planId;
    if (updates.allowSystemInfrastructure !== undefined) body.allowSystemInfrastructure = updates.allowSystemInfrastructure;
    if (updates.allowSelfManagedInfrastructure !== undefined) {
      body.allowSelfManagedInfrastructure = updates.allowSelfManagedInfrastructure;
    }
    if (updates.disableCrossCloudCatalogMounts !== undefined) {
      body.disableCrossCloudCatalogMounts = updates.disableCrossCloudCatalogMounts;
    }
    if (updates.disableCrossRegionCatalogMounts !== undefined) {
      body.disableCrossRegionCatalogMounts = updates.disableCrossRegionCatalogMounts;
    }
    if (updates.supportSlackWebhookUrl !== undefined) body.supportSlackWebhookUrl = updates.supportSlackWebhookUrl;
    if (updates.maxCpuQuotaOverride !== undefined) body.maxCpuQuotaOverride = updates.maxCpuQuotaOverride;
    if (updates.billingExempt !== undefined) body.billingExempt = updates.billingExempt;
    if (updates.trialCreditUsd !== undefined) body.trialCreditUsd = updates.trialCreditUsd;
    if (updates.trialCreditExpiresDays !== undefined) body.trialCreditExpiresDays = updates.trialCreditExpiresDays;
    if (updates.stopWhenCreditExhausted !== undefined) body.stopWhenCreditExhausted = updates.stopWhenCreditExhausted;
    if (updates.idleStopRequiredOverride !== undefined) body.idleStopRequiredOverride = updates.idleStopRequiredOverride;

    const response = await apiClient.patch<ApiOrganization>(`/organizations/${id}`, body);
    return normalize(response.data);
  },
};

export interface OrganizationDeletionPreview {
  id: string; name: string; label: string; protected: boolean;
  clusters: string[]; catalogs: string[]; managedDataCatalogs: string[]; databaseFiles: string[];
  projects: number; environments: number; members: number; subscriptionStatus?: string | null;
}
export interface OrganizationDeletionResult {
  id: string; name: string; deleted: boolean;
  removed: { clusters: string[]; catalogs: string[]; databaseFiles: string[]; storageBuckets: number };
  membersRemoved: number; subscriptionCanceled?: string | null;
}
